import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { MiddlewareEntry } from "@/api/admin-api"

import { Layers } from "@/components/icons"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"

import { CollapsibleManagementSection } from "./collapsible-management-section"
import { EditMiddlewareDialog } from "./edit-middleware-dialog"
import { MiddlewareTable } from "./middleware-table"
import {
  useAdminAgentsQuery,
  useAgentMiddlewareQuery,
  useCreateMiddlewareMutation,
  useDeleteMiddlewareMutation,
  useReplaceMiddlewareRawMutation,
  useUpdateMiddlewareMutation,
} from "../hooks/admin-queries"

/**
 * "Agent middleware" management section.
 *
 * Renders the per-agent middleware chain — a first-class table + Form/Raw
 * edit dialog. Previously the admin had to edit ``system_middleware``
 * directly in Cosmos.
 */
export function MiddlewareManagementSection() {
  const { t } = useTranslation()
  const { data: agents, isLoading: agentsLoading } = useAdminAgentsQuery()
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null)
  const currentAgent = selectedAgent || agents?.[0] || null

  const { data: middlewareData, isLoading: middlewareLoading } = useAgentMiddlewareQuery(
    currentAgent,
    !!currentAgent
  )

  const [editingEntry, setEditingEntry] = useState<MiddlewareEntry | null>(null)
  const [isAddingMiddleware, setIsAddingMiddleware] = useState(false)

  const createMutation = useCreateMiddlewareMutation(currentAgent ?? "")
  const updateMutation = useUpdateMiddlewareMutation(currentAgent ?? "")
  const deleteMutation = useDeleteMiddlewareMutation(currentAgent ?? "")
  const replaceRawMutation = useReplaceMiddlewareRawMutation(currentAgent ?? "")

  const handleToggleMiddleware = async (entry: MiddlewareEntry, enabled: boolean) => {
    if (!currentAgent) return
    try {
      await updateMutation.mutateAsync({
        middlewareId: entry.id,
        entry: { ...entry, enabled },
      })
      toast.success(
        t("adminDashboard.systemConfig.toastMiddlewareUpdated", {
          defaultValue: "Middleware updated",
        })
      )
    } catch (err) {
      toast.error(
        t("adminDashboard.systemConfig.toastMiddlewareUpdateFailed", {
          defaultValue: "Failed to update middleware",
        }),
        {
          description: err instanceof Error ? err.message : undefined,
        }
      )
    }
  }

  const handleDeleteMiddleware = async (entry: MiddlewareEntry) => {
    if (!currentAgent) return
    if (
      !confirm(
        t("adminDashboard.systemConfig.deleteConfirm", {
          defaultValue: "Delete middleware entry {{id}}? This cannot be undone.",
          id: entry.id,
        })
      )
    )
      return
    try {
      await deleteMutation.mutateAsync(entry.id)
      toast.success(
        t("adminDashboard.systemConfig.toastMiddlewareDeleted", {
          defaultValue: "Middleware deleted",
        })
      )
    } catch (err) {
      toast.error(
        t("adminDashboard.systemConfig.toastMiddlewareDeleteFailed", {
          defaultValue: "Failed to delete middleware",
        }),
        {
          description: err instanceof Error ? err.message : undefined,
        }
      )
    }
  }

  const handleFormSubmit = async (next: MiddlewareEntry) => {
    if (!currentAgent) return
    try {
      if (editingEntry) {
        await updateMutation.mutateAsync({ middlewareId: editingEntry.id, entry: next })
      } else {
        await createMutation.mutateAsync(next)
      }
      setEditingEntry(null)
      setIsAddingMiddleware(false)
      toast.success(
        editingEntry
          ? t("adminDashboard.systemConfig.toastMiddlewareUpdated", {
              defaultValue: "Middleware updated",
            })
          : t("adminDashboard.systemConfig.toastMiddlewareAdded", {
              defaultValue: "Middleware added",
            })
      )
    } catch (err) {
      toast.error(
        t("adminDashboard.systemConfig.toastSaveFailed", { defaultValue: "Save failed" }),
        {
          description: err instanceof Error ? err.message : undefined,
        }
      )
      throw err
    }
  }

  const handleRawSave = async (next: MiddlewareEntry, reason: string) => {
    if (!currentAgent) return
    if (!editingEntry) return
    if (!middlewareData?.hash) {
      throw new Error(
        t("adminDashboard.systemConfig.missingHashError", {
          defaultValue: "Missing hash — reload the page and try again",
        })
      )
    }
    // For single-entry raw edits we route through replaceMiddlewareRaw with
    // the whole list so the optimistic-concurrency hash check is authoritative.
    // Match on the ORIGINAL id (editingEntry.id), not next.id — otherwise a
    // raw re-key would fail to match any existing entry and the edit would
    // be silently dropped. The banner UI explicitly invites re-keying via
    // Raw mode, so this path must support it.
    const originalId = editingEntry.id
    const newList = middlewareData.middleware.map((entry) =>
      entry.id === originalId ? next : entry
    )
    try {
      await replaceRawMutation.mutateAsync({
        middleware: newList,
        expectedHash: middlewareData.hash,
        reason,
      })
      setEditingEntry(null)
      toast.success(
        t("adminDashboard.systemConfig.toastMiddlewareSavedRaw", {
          defaultValue: "Middleware saved (raw)",
        })
      )
    } catch (err) {
      toast.error(
        t("adminDashboard.systemConfig.toastRawSaveFailed", { defaultValue: "Raw save failed" }),
        {
          description: err instanceof Error ? err.message : undefined,
        }
      )
      throw err
    }
  }

  const mutationBusy =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    replaceRawMutation.isPending

  return (
    <CollapsibleManagementSection
      icon={<Layers className="size-5" />}
      id="middleware"
      storageKey="admin-management-middleware-expanded"
      title={t("adminDashboard.systemConfig.middlewareSectionTitle", {
        defaultValue: "Agent middleware",
      })}
    >
      <div className="space-y-3">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-muted-foreground text-xs">
            {t("adminDashboard.systemConfig.middlewareSectionDescription", {
              defaultValue:
                "The per-agent middleware chain applied to every invocation. Previously only editable by hand-editing Cosmos — now has a first-class UI.",
            })}
          </p>
          <div className="flex items-center gap-2">
            <Select
              disabled={agentsLoading || !agents?.length}
              value={currentAgent ?? undefined}
              onValueChange={setSelectedAgent}
            >
              <SelectTrigger className="w-40">
                <SelectValue
                  placeholder={t("adminDashboard.systemConfig.selectAgentPlaceholder", {
                    defaultValue: "Select agent",
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                {agents?.map((agent) => (
                  <SelectItem key={agent} value={agent}>
                    {agent}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </header>

        {(() => {
          if (middlewareLoading) {
            return (
              <div className="space-y-2">
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
              </div>
            )
          }
          if (!currentAgent) {
            return (
              <p className="text-muted-foreground text-sm">
                {t("adminDashboard.systemConfig.noAgents", {
                  defaultValue: "No agents discovered.",
                })}
              </p>
            )
          }
          return (
            <MiddlewareTable
              agentName={currentAgent}
              disableAll={mutationBusy}
              isDeleting={deleteMutation.isPending ? deleteMutation.variables : undefined}
              middleware={middlewareData?.middleware ?? []}
              isUpdating={
                updateMutation.isPending ? updateMutation.variables.middlewareId : undefined
              }
              onDelete={handleDeleteMiddleware}
              onToggleStatus={handleToggleMiddleware}
              onAdd={() => {
                setEditingEntry(null)
                setIsAddingMiddleware(true)
              }}
              onEdit={(entry) => {
                setEditingEntry(entry)
                setIsAddingMiddleware(false)
              }}
            />
          )
        })()}
      </div>

      <EditMiddlewareDialog
        agentName={currentAgent ?? ""}
        entry={editingEntry}
        isOpen={isAddingMiddleware || editingEntry !== null}
        isPending={mutationBusy}
        onRawSave={handleRawSave}
        onSubmit={handleFormSubmit}
        onOpenChange={(open) => {
          if (!open) {
            setIsAddingMiddleware(false)
            setEditingEntry(null)
          }
        }}
      />
    </CollapsibleManagementSection>
  )
}
